import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react'

import { APP_TITLE } from 'src/platform/config'

const TitleContext = createContext<
  ((id: string, title?: string) => void) | undefined
>(undefined)

// React hoists head elements itself. Keep a single title at the root so page
// titles never compete with the fallback (multiple <title>s are undefined).
export const HeadProvider = ({
  titleTemplate,
  children,
}: {
  titleTemplate?: string
  children?: ReactNode
}) => {
  const [titles, setTitles] = useState(() => new Map<string, string>())
  const setTitle = useCallback((id: string, title?: string) => {
    setTitles((previous) => {
      const next = new Map(previous)
      if (title === undefined) next.delete(id)
      else next.set(id, title)
      return next
    })
  }, [])
  const title = [...titles.values()].at(-1) ?? APP_TITLE
  const formatted = titleTemplate
    ? titleTemplate
        .replace(/%AppTitle/g, () => APP_TITLE)
        .replace(/%PageTitle/g, () => title)
    : title
  return (
    <TitleContext.Provider value={setTitle}>
      <title>{formatted}</title>
      {children}
    </TitleContext.Provider>
  )
}

const PageTitle = ({ title }: { title: string }) => {
  const setTitle = useContext(TitleContext)
  const id = useId()
  useLayoutEffect(() => {
    if (!setTitle) return
    setTitle(id, title)
    return () => setTitle(id)
  }, [id, setTitle, title])
  return setTitle ? null : <title>{title}</title>
}

export interface MetaTagsProps {
  title?: string
  description?: string
  ogType?: string
  children?: ReactNode
}

export const MetaTags = ({
  title,
  description,
  ogType = 'website',
  children,
}: MetaTagsProps) => (
  <>
    {title && (
      <>
        <PageTitle title={title} />
        <meta property="og:title" content={title} />
        <meta property="twitter:title" content={title} />
      </>
    )}
    {description && (
      <>
        <meta name="description" content={description} />
        <meta name="twitter:description" content={description} />
        <meta property="og:description" content={description} />
      </>
    )}
    <meta property="og:type" content={ogType} />
    <meta property="twitter:card" content="summary_large_image" />
    <meta property="twitter:image" />
    {children}
  </>
)

export interface MetadataProps {
  title?: string | string[]
  description?: string
  children?: ReactNode
}

export const Metadata = ({ title, description, children }: MetadataProps) => {
  const text = Array.isArray(title) ? title.at(-1) : title
  return (
    <>
      {text !== undefined && <PageTitle title={text} />}
      {title !== undefined &&
        [title]
          .flat()
          .map((value, index) => (
            <meta key={index} name="title" content={value} />
          ))}
      {description !== undefined && (
        <meta name="description" content={description} />
      )}
      {children}
    </>
  )
}
